<template>
  <div class="py-3 py-md-4 pb-5 mb-5">

    <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:10px;">
      <div>
        <h3 class="text-primary font-weight-bold mb-0">Today</h3>
        <div class="text-secondary small">{{ todayLabel }}</div>
      </div>
      <div class="d-flex" style="gap:8px;">
        <button class="btn btn-outline-secondary" :disabled="loading" @click="load"><i class="bi bi-arrow-clockwise"></i></button>
        <a href="/make-reservations" class="btn btn-primary"><i class="bi bi-plus-lg mr-1"></i>New booking</a>
      </div>
    </div>

    <div v-if="loading && !d" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <template v-else-if="d">
      <!-- Summary -->
      <div class="row mb-3" style="row-gap:14px;">
        <div v-for="s in stats" :key="s.label" class="col-4 col-md-2">
          <div class="text-secondary small">{{ s.label }}</div>
          <div class="font-weight-bold text-dark" style="font-size:20px;">{{ s.value }}</div>
        </div>
      </div>

      <div v-if="!d.summary.rooms" class="alert alert-secondary">
        No rooms yet. Mark products as <strong>Reservable</strong> in Inventory to list them here as rooms.
      </div>

      <!-- Lists -->
      <ul class="nav nav-pills mb-3 flex-nowrap overflow-auto">
        <li v-for="t in tabs" :key="t.key" class="nav-item">
          <a href="#" class="nav-link text-nowrap" :class="{ active: tab === t.key }" @click.prevent="tab = t.key">
            {{ t.label }} <span class="badge" :class="tab === t.key ? 'badge-light' : 'badge-secondary'">{{ d[t.key].length }}</span>
          </a>
        </li>
      </ul>

      <div class="list-group mb-4">
        <div v-for="r in d[tab]" :key="r.id" class="list-group-item px-3 py-3">
          <div class="d-flex justify-content-between align-items-start" style="gap:10px;">
            <div style="min-width:0;">
              <div class="font-weight-bold text-dark text-truncate">{{ r.guest || 'Guest' }}</div>
              <div class="text-secondary small">
                {{ r.room }} · {{ r.nights }} night{{ r.nights === 1 ? '' : 's' }} · {{ guests(r) }}
              </div>
              <div class="small mt-1">
                <span class="text-secondary">{{ shortDate(r.check_in) }} → {{ shortDate(r.check_out) }}</span>
                <span v-if="r.late" class="badge badge-danger ml-1">Late arrival</span>
                <span v-if="r.overstay" class="badge badge-danger ml-1">Past check-out</span>
                <span v-if="r.group_parent_id" class="badge badge-secondary ml-1">Group</span>
              </div>
            </div>
            <div class="text-right flex-shrink-0">
              <div v-if="r.balance > 0" class="text-danger font-weight-bold small">Owes {{ money(r.balance) }}</div>
              <div v-else-if="r.balance === 0" class="text-secondary small">Paid</div>
            </div>
          </div>
          <div class="d-flex flex-wrap mt-2" style="gap:8px;">
            <button v-if="tab === 'arrivals'" class="btn btn-primary btn-sm" @click="ask('check-in', r)">
              <i class="bi bi-box-arrow-in-right mr-1"></i>Check in
            </button>
            <button v-if="tab !== 'arrivals'" class="btn btn-sm" :class="tab === 'departures' ? 'btn-primary' : 'btn-outline-primary'" @click="ask('check-out', r)">
              <i class="bi bi-box-arrow-right mr-1"></i>Check out
            </button>
            <a :href="'/reservations?reservation-id=' + (r.group_parent_id || r.id)" class="btn btn-outline-secondary btn-sm">
              <i class="bi bi-cash-coin mr-1"></i>{{ r.balance > 0 ? 'Take payment' : 'Open' }}
            </a>
            <a v-if="r.phone" :href="'tel:' + r.phone" class="btn btn-outline-secondary btn-sm"><i class="bi bi-telephone"></i></a>
          </div>
        </div>
        <div v-if="!d[tab].length" class="list-group-item text-secondary text-center py-4">{{ emptyText }}</div>
      </div>

      <!-- Room board -->
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-2" style="gap:8px;">
        <h5 class="font-weight-bold text-dark mb-0">Rooms</h5>
        <div class="d-flex flex-wrap small text-secondary" style="gap:10px;">
          <span v-for="l in legend" :key="l.state" class="d-inline-flex align-items-center" style="gap:4px;">
            <span class="d-inline-block rounded" :class="l.cls" style="width:12px;height:12px;"></span>{{ l.label }}
          </span>
        </div>
      </div>
      <div class="row" style="row-gap:10px;margin-left:-5px;margin-right:-5px;">
        <div v-for="room in d.rooms" :key="room.id" class="col-6 col-sm-4 col-md-3 col-xl-2" style="padding-left:5px;padding-right:5px;">
          <component
            :is="room.reservation_id ? 'a' : 'div'"
            :href="room.reservation_id ? '/reservations?reservation-id=' + room.reservation_id : null"
            class="d-block rounded p-2 h-100 text-decoration-none"
            :class="roomClass(room.state)"
            style="min-height:76px;"
          >
            <div class="font-weight-bold text-truncate">{{ room.name }}</div>
            <div class="small text-truncate" style="opacity:.85;">{{ stateLabel(room) }}</div>
            <div v-if="room.guest" class="small text-truncate" style="opacity:.85;">{{ room.guest }}</div>
          </component>
        </div>
      </div>
    </template>

    <!-- Confirm check-in / check-out -->
    <b-modal v-model="confirm.show" :title="confirm.action === 'check-in' ? 'Check in guest?' : 'Check out guest?'" centered
             ok-variant="primary" :ok-title="confirm.action === 'check-in' ? 'Check in' : 'Check out'" :busy="confirm.busy" @ok.prevent="act">
      <template v-if="confirm.r">
        <p class="mb-1"><strong>{{ confirm.r.guest }}</strong> · {{ confirm.r.room }}</p>
        <p class="text-secondary small mb-0">{{ shortDate(confirm.r.check_in) }} → {{ shortDate(confirm.r.check_out) }}</p>
        <p v-if="confirm.action === 'check-out' && confirm.r.balance > 0" class="text-danger small mt-2 mb-0">
          This booking still owes {{ money(confirm.r.balance) }}. Take payment first, or check out anyway.
        </p>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'

/** Hotel / BnB front desk: today's arrivals, in-house, departures and the room board. */
export default {
  name: 'ReservationsToday',

  data() {
    return {
      d: null,
      loading: false,
      error: null,
      tab: 'arrivals',
      confirm: { show: false, action: null, r: null, busy: false },
      tabs: [
        { key: 'arrivals', label: 'Arriving' },
        { key: 'in_house', label: 'In house' },
        { key: 'departures', label: 'Leaving' },
      ],
      legend: [
        { state: 'free', label: 'Free', cls: 'bg-white border' },
        { state: 'arriving', label: 'Arriving', cls: 'border border-primary' },
        { state: 'occupied', label: 'Occupied', cls: 'bg-primary' },
        { state: 'leaving', label: 'Leaving', cls: 'bg-dark' },
        { state: 'blocked', label: 'Not available', cls: 'bg-secondary' },
      ],
    }
  },

  computed: {
    todayLabel() { return moment().format('dddd, D MMMM') },
    stats() {
      const s = this.d.summary
      return [
        { label: 'Occupancy', value: s.occupancy + '%' },
        { label: 'Occupied', value: s.occupied },
        { label: 'Free', value: s.free },
        { label: 'Arriving', value: s.arriving },
        { label: 'Leaving', value: s.leaving },
        { label: 'Rooms', value: s.rooms },
      ]
    },
    emptyText() {
      return { arrivals: 'No arrivals today.', in_house: 'No guests staying on.', departures: 'No departures today.' }[this.tab]
    },
  },

  mounted() {
    this.load()
    // If someone is leaving and nobody is arriving, open on Leaving.
    this.$once('loaded', () => {
      if (!this.d.arrivals.length && this.d.departures.length) this.tab = 'departures'
    })
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get('/api/reservations/today')
        this.d = data
        this.$emit('loaded')
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not load today.'
      } finally {
        this.loading = false
      }
    },

    ask(action, r) {
      this.confirm = { show: true, action, r, busy: false }
    },

    async act() {
      const { action, r } = this.confirm
      this.confirm.busy = true
      try {
        await axios.post(`/api/reservations/${action}/${r.id}`)
        this.confirm.show = false
        this.$toasts && this.$toasts.push({ type: 'success', message: `${r.guest || 'Guest'} ${action === 'check-in' ? 'checked in' : 'checked out'}.` })
        this.load()
      } catch (e) {
        const errors = e.response?.data?.errors
        const msg = errors ? Object.values(errors).flat()[0] : (e.response?.data?.message || 'Could not update the booking.')
        this.$toasts && this.$toasts.push({ type: 'error', message: msg })
      } finally {
        this.confirm.busy = false
      }
    },

    roomClass(state) {
      return {
        free: 'bg-white border text-dark',
        arriving: 'bg-white border border-primary text-primary',
        occupied: 'bg-primary text-white',
        leaving: 'bg-dark text-white',
      }[state] || 'bg-secondary text-white'
    },

    stateLabel(room) {
      if (room.state === 'occupied') return 'Until ' + this.shortDate(room.check_out)
      return { free: 'Free', arriving: 'Arriving today', leaving: 'Leaving today', cleaning: 'Cleaning', maintenance: 'Maintenance', blocked: 'Blocked' }[room.state] || room.state
    },

    guests(r) {
      const parts = [`${r.adults} adult${r.adults === 1 ? '' : 's'}`]
      if (r.children) parts.push(`${r.children} child${r.children === 1 ? '' : 'ren'}`)
      return parts.join(', ')
    },

    shortDate(d) { return moment(d).format('ddd D MMM') },
    money(v) { return 'KES ' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 }) },
  },
}
</script>
